import { z } from "zod";

// The athlete's minimum-age declaration (#175, Antenore 19/10): one checkbox,
// «I am at least N, or a parent knows I use Metria and allows it», given once
// and dated. Nothing is verified and no age is ever compared in code: the
// athlete reads the sentence and ticks it (EDPB 05/2020, low risk).
//
// Lives on the Profile row, server-managed: written only by POST and DELETE
// /profiles/age-declaration, so an old client's PUT cannot erase it. Not in
// NewProfile, not in the coach projection, not in the connector: it is legal
// data about the athlete, not training data.

/** The age the sentence names (minors-and-consent.md §4.2: never an age
 *  literal at a callsite; a per-country map goes here if a legal reading asks). */
export const DEFAULT_MIN_AGE = 16;

/** Client to server: when the athlete ticked, on the device clock. */
export const NewAgeDeclaration = z.object({ at: z.iso.datetime() });
export type NewAgeDeclaration = z.infer<typeof NewAgeDeclaration>;

/** As stored: `recordedAt` is the server's clock at receipt. */
export const AgeDeclaration = NewAgeDeclaration.extend({
  recordedAt: z.iso.datetime(),
});
export type AgeDeclaration = z.infer<typeof AgeDeclaration>;

/** Two hundred events is a hundred declare and withdraw cycles. */
export const AGE_DECLARATION_LOG_MAX = 200;

/** One entry of the append-only record (#383): each declaration written and
 *  each withdrawal, with the tap and the server clock. A withdrawal names the
 *  `at` of the declaration it withdrew, when that one was readable. */
export const AgeDeclarationEvent = z.discriminatedUnion("kind", [
  AgeDeclaration.extend({ kind: z.literal("declared") }),
  AgeDeclaration.extend({
    kind: z.literal("withdrawn"),
    declaredAt: z.iso.datetime().optional().catch(undefined),
    /** The server clock of the withdrawn declaration, when readable. */
    declaredRecordedAt: z.iso.datetime().optional().catch(undefined),
  }),
]);
export type AgeDeclarationEvent = z.infer<typeof AgeDeclarationEvent>;

export const AgeDeclarationLog = z.array(AgeDeclarationEvent).max(AGE_DECLARATION_LOG_MAX);
export type AgeDeclarationLog = z.infer<typeof AgeDeclarationLog>;

/** Whether a profile document (as read from the API) holds a valid
 *  declaration. A document that does not parse as one counts as none. */
export const hasDeclaredAge = (doc: unknown): boolean =>
  typeof doc === "object" &&
  doc !== null &&
  AgeDeclaration.safeParse((doc as { ageDeclaration?: unknown }).ageDeclaration)
    .success;

/** Whether this document asks for the declaration: a profile the API returned
 *  (it carries its id) with no valid one. Not read yet, a failed read or a
 *  404's empty document ask nothing, so a network error never locks anyone out. */
export const needsAgeDeclaration = (doc: unknown): boolean =>
  typeof doc === "object" &&
  doc !== null &&
  typeof (doc as { id?: unknown }).id === "string" &&
  !hasDeclaredAge(doc);

/** A monotonic reading in ms for ordering gestures and reads on this device
 *  (a wall-clock step cannot reorder them). Not a date: never stored or sent. */
export const monotonicNowMs = (): number => {
  const p = (globalThis as { performance?: { now(): number } }).performance;
  return p !== undefined ? p.now() : Date.now();
};

/** A profile read from the server: whether it held the declaration, and when
 *  that read left (the client's clock). */
export type AgeRead = { declared: boolean; startedAt: number };
/** The athlete's own declare or withdraw on this device, and when it landed. */
export type AgeGesture = { declared: boolean; at: number };

/** Whether this device treats the account as declared (#383). The server read
 *  decides, so a change made on another device arrives with the next read.
 *  A gesture made here after that read left decides instead: the read may
 *  predate the write. Undefined while neither exists. */
export const resolveAgeDeclared = (
  read: AgeRead | null,
  gesture: AgeGesture | null,
): boolean | undefined => {
  if (gesture !== null && (read === null || gesture.at >= read.startedAt))
    return gesture.declared;
  return read?.declared;
};
